import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RetentionInfoBox from './RetentionInfoBox';
import RouletteRetentionTable from './RouletteRetentionTable';
import type { Dayjs } from 'dayjs';

interface RetentionDataPoint {
  day: string;
  rate: number;
}

interface RetentionDayData {
  userCount: number;
  ticketUserCount: number;
}

interface DailyRetentionRow {
  date: string;
  newUserCount: number;
  day1?: RetentionDayData;
  day2?: RetentionDayData;
  day3?: RetentionDayData;
  day4?: RetentionDayData;
  day5?: RetentionDayData;
  day6?: RetentionDayData;
  day7?: RetentionDayData;
}

interface RetentionTrendRow {
  date: string;
  newUserCount: number;
  day1?: number;
  day2?: number;
  day3?: number;
  day4?: number;
  day5?: number;
  day6?: number;
  day7?: number;
}

interface RouletteRetentionResponse {
  retentionTrend: RetentionTrendRow[];
  dailyRetention: DailyRetentionRow[];
}

interface RetentionLineChartProps {
  data?: RetentionDataPoint[];
  startDate: Dayjs;
  endDate: Dayjs;
  selectedApp?: string | null;
  retentionData: RouletteRetentionResponse | null;
  loading: boolean;
}

export default function RetentionLineChart({
  data,
  startDate,
  endDate,
  selectedApp,
  retentionData,
  loading,
}: RetentionLineChartProps) {
  const chartData: RetentionDataPoint[] = (() => {
    if (data) return data;
    if (!retentionData?.retentionTrend || retentionData.retentionTrend.length === 0) {
      return [1, 2, 3, 4, 5, 6, 7].map((i) => ({ day: `DAY ${i}`, rate: 0 }));
    }

    const trend = retentionData.retentionTrend[0];
    const newUserCount = trend.newUserCount || 0;
    const result: RetentionDataPoint[] = [];

    for (let i = 1; i <= 7; i++) {
      const dayKey = `day${i}` as keyof RetentionTrendRow;
      const count = trend[dayKey] as number | undefined;
      const rate = newUserCount > 0 && count ? (count / newUserCount) * 100 : 0;
      result.push({
        day: `DAY ${i}`,
        rate: Math.round(rate * 10) / 10,
      });
    }

    return result;
  })();

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ value: number }> }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 1,
            p: 1.5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="body2" sx={{ color: '#1f2a37', fontWeight: 600 }}>
            {payload[0].value}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1f2a37' }}>
          잔존율 추이
        </Typography>
        <Tooltip
          title="조회한 기간의 첫 날짜를 기준으로 리텐션 추이를 나타낸 차트입니다."
          arrow
          placement="right"
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ height: 300, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="day"
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 20, 40, 60, 80, 100]}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
            />
            <RechartsTooltip content={<CustomTooltip />} cursor={{ stroke: '#e5e7eb' }} />
            <Line
              type="monotone"
              dataKey="rate"
              stroke="#4A90E2"
              strokeWidth={2}
              fill="url(#colorRate)"
              dot={false}
              activeDot={{ r: 6, fill: '#4A90E2', stroke: '#fff', strokeWidth: 2 }}
            />
            <defs>
              <linearGradient id="colorRate" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4A90E2" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#4A90E2" stopOpacity={0} />
              </linearGradient>
            </defs>
          </LineChart>
        </ResponsiveContainer>
        </Box>
      )}

      <RetentionInfoBox />

      <RouletteRetentionTable
        startDate={startDate}
        endDate={endDate}
        selectedApp={selectedApp}
        retentionData={retentionData}
        loading={loading}
      />
    </Box>
  );
}
